import React, { useEffect } from "react";
import * as Icons from "@wix/wix-ui-icons-common";
import {
  Box,
  Button,
  Card,
  WixDesignSystemProvider,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Page,
  Loader,
  SectionHelper,
  Notification,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { ReactTagManager } from "react-gtm-ts";
import { useTranslation } from "react-i18next";
import { initAnalytics, track } from "./analytics";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

const CONTENT_LENGTH = 3;

const INTERVAL = 4000;

function App() {
  const { t } =useTranslation();
  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [connection, setConnection] = React.useState({} as any);
  const [isRequestedRefresh, setIsRequestedRefresh] = React.useState(false);
  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  } as any);

  const isFree = instanceData?.instance?.isFree !== false;

  const [index, setIndex] = React.useState<number>(0);
  const initialBannerState: boolean = !isUpgraded;
  const [isUpgradeBannerOpen, setIsUpgradeBannerOpen] = React.useState<boolean>(initialBannerState);

  useEffect(() => {
    const sliderInterval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % CONTENT_LENGTH);
    }, INTERVAL);

    return () => clearInterval(sliderInterval);
  }, []);

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
    initAnalytics();
    loadInstance();
    getConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const BASE_URL = "https://wix.certifiedcode.io/one-tiktok/_functions";
  const WIX_APP_ID = "e41e7464-3310-41be-9c20-d2bb0672dfc4";

  function loadInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: { Authorization: instance || "" },
    })
      .then((r) => r.json())
      .then((data) => {
        setInstanceData(data);
        const upgraded = data?.instance?.isFree === false;
        setIsUpgraded(upgraded);
        setIsUpgradeBannerOpen(!upgraded);
      })
      .catch(() => {});
  }

  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}`;

  const getConnection = () => {
    fetch(
      "https://wix.certifiedcode.io/one-tiktok/_functions/connection",
      {
        headers: {
          Authorization: instance || "",
        },
      }
    )
      .then((res) => res.json())
      .then((res) => {
        setConnection(res);
      });
  };

  const updateCount = () => {
    setIsRequestedRefresh(true);
    fetch("https://wix.certifiedcode.io/one-tiktok/_functions/count", {
      method: "POST",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((res) => res.json())
      .then((res) => {
        console.log(res);
      });
  };

  if (token) {
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t('loading')} />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  const nickname = connection?.meta?.data?.user?.nickname;
  const uniqueId = connection?.meta?.data?.user?.uniqueId;
  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Notification theme="premium" show={isUpgradeBannerOpen} type={"sticky"}>
        <Notification.TextLabel>
        {t('upgradeMessage', { content: t(`content.${index}`) })}
        </Notification.TextLabel>
        <Notification.ActionButton
          onClick={() => {
            track("upgrade_clicked"); window.open(upgradeUrl, "_blank");
          }}
        >
          {t('upgrade-button')}
        </Notification.ActionButton>
        <Notification.CloseButton
          onClick={() => setIsUpgradeBannerOpen(false)}
        />
      </Notification>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="840px" verticalAlign="middle">
              <MarketingPageLayoutContent
                title={t('tik-title')}
                content={t('tik-content')}
                actions={
                  <Box gap="SP2" direction="vertical">
                    <Box gap="SP2">
                      <Button
                        suffixIcon={<Icons.Edit />}
                        as="a"
                        href={`https://www.wix.com/editor/${instanceData?.site?.siteId}`}
                        target="_blank"
                      >
                        {t('tik-edit-button')}
                      </Button>
                      <Button
                        suffixIcon={<Icons.PremiumFilled />}
                        skin={isUpgraded ? "premium-light" : "premium"}
                        as="a"
                        href={upgradeUrl}
                        target="_blank"
                      >
                        {isUpgraded ? t('manage-plan-button') : t('upgrade-premium-button')}
                      </Button>
                    </Box>
                    
                    {connection?.isConnected && (
                      <SectionHelper
                        title={
                          connection?.isConnected
                            ?  t('connectedMessage', { nickname, uniqueId })
                            : t('not-connected-msg')
                        }
                        appearance="success"
                      >
                        <Box gap="SP2" direction="vertical">
                          {isUpgraded
                            ? t('count-msg')
                            : t('refresh-msg')
                            }
                          {isRequestedRefresh ? (
                            <Button disabled>{t('request-button')}</Button>
                          ) : (
                            <Button
                              disabled={!isUpgraded}
                              onClick={() => {
                                if (isUpgraded) {
                                  updateCount();
                                } else {
                                  track("upgrade_clicked"); window.open(upgradeUrl, "_blank");
                                }
                              }}
                            >
                              {t('refresh-now-button')}
                            </Button>
                          )}
                        </Box>
                      </SectionHelper>
                    )}
                  </Box>
                }
              />
            </Box>
          }
        />
      </Card>
    </WixDesignSystemProvider>
  );
}

export default App;
